import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { loginUser, checkAuth, loginVerify2fa } from "../utils/api";
import { StoreState } from "./storeType";
const SESSION_DURATION = 12 * 3600000;

export const useMainStore = create<StoreState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoggedOut: true,
      error: "",
      loading: false,
      loadingPage: false,
      intervalId: null,
      curr: "RUB",

      changeCurrency: (curr: string) => set({ curr }),

      /** Backend `detail` arrives both as a string and as an object. */
      describeAuthError: (err: any) => {
        if (!err.response) {
          return err.request
            ? "Connection error."
            : "An error occurred while sending the request";
        }
        const detail = err.response.data?.detail;
        if (detail && typeof detail === "object") {
          // Lockout after repeated bad codes carries locked_until.
          if (detail.locked_until) {
            const until = new Date(detail.locked_until).toLocaleTimeString(
              "en-GB",
              { hour: "2-digit", minute: "2-digit" },
            );
            return `Too many invalid codes. Try again after ${until}`;
          }
          return detail.message || "Unknown error";
        }
        return `Error: ${err.response.status} - ${detail || "Unknown error"}`;
      },

      /** Opens the session after a successful sign-in — shared by both steps. */
      completeLogin: (user: string | null) => {
        localStorage.setItem(
          "auth_expiration",
          String(Date.now() + SESSION_DURATION),
        );
        get().checkAuthStatus();
        get().startSessionCheck();
        set({ isAuthenticated: true, user, isLoggedOut: false });
      },

      login: async (username, password) => {
        set({ loading: true, error: "" });

        try {
          const response = await loginUser({ username, password });

          // Password accepted but no access yet: instead of the access cookie
          // the backend sets a short-lived one for the second step.
          if (response.data?.twofa_required) {
            return { ok: false, twofaRequired: true };
          }

          get().completeLogin(response.data.user);
          return { ok: true, twofaRequired: false };
        } catch (err: any) {
          set({ error: get().describeAuthError(err) });
          return { ok: false, twofaRequired: false };
        } finally {
          set({ loading: false });
        }
      },

      verifyTwoFactor: async (code: string) => {
        set({ loading: true, error: "" });
        try {
          const response = await loginVerify2fa(code);
          get().completeLogin(response.data?.user ?? null);
          return { ok: true };
        } catch (err: any) {
          set({ error: get().describeAuthError(err) });
          return { ok: false };
        } finally {
          set({ loading: false });
        }
      },

      logout: () => {
        clearTimeout(get().intervalId!);
        localStorage.removeItem("auth_expiration");

        set({
          isAuthenticated: false,
          user: null,
          isLoggedOut: true,
          intervalId: null,
          loadingPage: false,
        });
      },

      checkAuthStatus: () => {
        const expirationTime = Number(localStorage.getItem("auth_expiration"));
        if (Date.now() > expirationTime) {
          get().logout();
          return;
        }

        set({ loadingPage: true });
        checkAuth()
          .then((response) => {
            if (!response.data.user) {
              get().logout();
              return;
            }
            set({
              isAuthenticated: true,
              user: response.data.user,
              isLoggedOut: false,
            });
          })
          .catch(() => {
            get().logout();
          })
          .finally(() => set({ loadingPage: false }));
      },

      startSessionCheck: () => {
        if (get().intervalId) return;

        const expirationTime = Number(localStorage.getItem("auth_expiration"));
        const timeLeft = expirationTime - Date.now();

        if (timeLeft <= 0) {
          get().logout();
          return;
        }

        const timeoutId = setTimeout(() => {
          get().logout();
        }, timeLeft);

        set({ intervalId: timeoutId });
      },
    }),
    {
      name: "store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isLoggedOut: state.isLoggedOut,
        curr: state.curr,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.checkAuthStatus();
        }
      },
    },
  ),
);
